import { useState, useMemo } from 'react'
import { Box, Typography } from '@mui/material'
import { zIndexes } from '@/utils/zIndexes'

interface TrendGraphProps {
  data: { date: string | Date; count: number }[] | undefined
  color: string
  gradientId: string
  width?: number
  height?: number
}

export const TrendGraph = ({ data, color, gradientId, width = 100, height = 60 }: TrendGraphProps) => {
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number
    y: number
    count: number
    date: string
  } | null>(null)

  // Generate path and points data
  const { path, points } = useMemo(() => {
    if (!data || data.length === 0) {
      // Default path if no data
      const defaultPath = 'M 0,30 L 20,25 L 40,20 L 60,15 L 80,10 L 100,5 L 100,40 L 0,40 Z'
      return { path: defaultPath, points: [] }
    }

    const counts = data.map((d) => d.count)
    const maxCount = Math.max(...counts, 1)
    const minCount = Math.min(...counts, 0)
    const range = maxCount - minCount || 1

    const calculatedPoints = data.map((value, index) => {
      const x = (index / (data.length - 1)) * width
      const y = height - ((value.count - minCount) / range) * height
      return {
        x,
        y,
        count: value.count,
        date: typeof value.date === 'string' ? value.date : value.date.toISOString(),
      }
    })

    let pathStr = `M ${calculatedPoints[0].x} ${calculatedPoints[0].y}`
    for (let i = 1; i < calculatedPoints.length; i++) {
      pathStr += ` L ${calculatedPoints[i].x} ${calculatedPoints[i].y}`
    }
    // Close the path for the filled area
    pathStr += ` L ${width},${height} L 0,${height} Z`

    return { path: pathStr, points: calculatedPoints }
  }, [data, width, height])

  // Get the line path (without the closing part for fill)
  const linePath = useMemo(() => {
    return path.split(` L ${width},${height}`)[0]
  }, [path, width, height])

  // Format date for display
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', height }}>
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ display: 'block' }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Filled area */}
        <path d={path} fill={`url(#${gradientId})`} />

        {/* Line stroke */}
        <path d={linePath} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* Invisible hover areas for each point */}
        {points.map((point, index) => (
          <g key={index}>
            {/* Invisible larger circle for easier hovering */}
            <circle
              cx={point.x}
              cy={point.y}
              r="8"
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHoveredPoint(point)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
          </g>
        ))}
      </svg>

      {/* Tooltip */}
      {hoveredPoint && (
        <Box
          sx={{
            position: 'absolute',
            left: `${(hoveredPoint.x / width) * 100}%`,
            top: `${(hoveredPoint.y / height) * 100}%`,
            transform: 'translate(-50%, -120%)',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            pointerEvents: 'none',
            zIndex: zIndexes.tooltip,
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-4px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 0,
              height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '4px solid rgba(0, 0, 0, 0.85)',
            },
          }}
        >
          <Typography sx={{ fontSize: '14px', fontWeight: 700, mb: 0.25 }}>
            {hoveredPoint.count.toLocaleString()}
          </Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 400, opacity: 0.9 }}>
            {formatDate(hoveredPoint.date)}
          </Typography>
        </Box>
      )}
    </Box>
  )
}
